(() => {
  if (customElements.get('aw-fit-finder')) return;
  class AWFitFinder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      try {
        this.data = JSON.parse(this.querySelector('[data-fit-data]').textContent);
        this.copy = JSON.parse(this.querySelector('[data-fit-copy]').textContent);
      } catch { return; }
      if (!globalThis.AWFitLogic) return;
      this.sizes = AWFitLogic.orderedSizes(this.data.sizes, this.data.category);
      if (!this.sizes || this.data.variants.length >= 250) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog.showModal) return;
      this.ready = true;
      this.hidden = false;
      this.answers = {};
      this.step = 0;
      this.addEventListener('click', event => this.onClick(event));
      this.dialog.addEventListener('close', () => this.querySelector('[data-fit-open]').focus());
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); });
    }
    disconnectedCallback() { clearTimeout(this.selectionTimer); }
    el(key) { return this.querySelector('[data-fit-' + key + ']'); }
    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-fit-open')) { this.step = 0; this.answers = {}; this.render(); this.dialog.showModal(); this.el('title').focus(); }
      if (button.hasAttribute('data-fit-close') || button.hasAttribute('data-fit-done')) this.dialog.close();
      if (button.hasAttribute('data-fit-back')) { this.step = Math.max(0, this.step - 1); this.render(); }
      if (button.hasAttribute('data-fit-restart')) { this.step = 0; this.answers = {}; this.render(); }
      if (button.hasAttribute('data-fit-unlisted')) this.showResult({ status: 'not_listed' });
      if (button.hasAttribute('data-fit-answer')) {
        this.answers[this.questions()[this.step].key] = button.dataset.fitAnswer;
        this.step++;
        if (this.step < this.questions().length) this.render();
        else this.showResult(AWFitLogic.recommend({ ...this.data, answers: this.answers }));
      }
    }
    questions() {
      const c = this.copy;
      const questions = [
        { key: 'usual', title: this.data.category === 'bottoms' ? c.usual_bottom : c.usual_top, help: c.usual_help, answers: this.sizes.map(s => [s, s]) },
        { key: 'body', title: this.data.category === 'bottoms' ? c.body_bottom : c.body_top, help: c.body_help, answers: [['slim', c.body_slim], ['medium', c.body_medium], ['fuller', c.body_fuller]] },
        { key: 'feel', title: this.data.category === 'bottoms' ? c.feel_bottom : c.feel_top, help: c.feel_help, answers: [['comfortable', c.comfortable], ['sometimes', c.sometimes], ['tight', c.tight]] },
        { key: 'fit', title: c.fit, help: c.fit_help, answers: [['close', c.close], ['regular', c.regular], ['roomy', c.roomy]] }
      ];
      if (this.data.category === 'outerwear') questions.push({ key: 'layer', title: c.layer, help: c.layer_help, answers: [['no', c.layer_no], ['yes', c.layer_yes]] });
      return questions;
    }
    bodyIllustration(shape) {
      const width = { slim: 20, medium: 27, fuller: 34 }[shape];
      const left = 60 - width;
      const right = 60 + width;
      const hips = width + 1;
      const top = this.data.category !== 'bottoms';
      const viewBox = top ? '0 0 120 190' : '0 76 120 114';
      const shirt = `M49 42 L43 45 Q${left - 7} 47 ${left - 9} 59 L${left - 15} 98 Q${left - 15} 104 ${left - 8} 104 L${left - 2} 78 Q${left - 3} 98 ${left} 113 Q60 119 ${right} 113 Q${right + 3} 98 ${right + 2} 78 L${right + 8} 104 Q${right + 15} 104 ${right + 15} 98 L${right + 9} 59 Q${right + 7} 47 77 45 L71 42`;
      const trousers = `M${left} 113 Q60 119 ${right} 113 L${60 + hips} 131 L${right - 1} 178 L67 178 L60 135 L53 178 L${left + 1} 178 L${60 - hips} 131 Z`;
      return `<svg viewBox="${viewBox}" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="${trousers}" fill="${top ? '#e3e4e1' : '#d7dfdc'}"/><path d="M${left + 1} 122 Q60 128 ${right - 1} 122 M60 126 V135" fill="none"/><path d="${shirt} Z" fill="${top ? '#e7dfd2' : '#f0ebe2'}"/><path d="M49 36 V43 Q60 53 71 43 V36" fill="#fffaf3"/><ellipse cx="60" cy="24" rx="13" ry="16" fill="#fffaf3"/><path d="M43 45 Q60 62 77 45 M${left + 3} 109 Q60 114 ${right - 3} 109" fill="none"/><path d="M${left + 1} 178 L${left - 3} 184 H53 V178 M67 178 V184 H${right + 3} L${right - 1} 178" fill="#fffaf3"/></g></svg>`;
    }
    render() {
      clearTimeout(this.selectionTimer);
      const questions = this.questions();
      const q = questions[this.step];
      this.el('result').hidden = true;
      this.el('options').hidden = false;
      this.el('progress').hidden = false;
      this.el('progress').textContent = `${this.copy.step} ${this.step + 1} ${this.copy.of} ${questions.length}`;
      this.el('meter').hidden = false;
      this.el('meter').style.setProperty('--aw-fit-progress', `${((this.step + 1) / questions.length) * 100}%`);
      this.el('title').textContent = q.title;
      this.el('help').textContent = q.help;
      this.el('help').hidden = false;
      const options = this.el('options');
      options.replaceChildren();
      options.className = 'aw-fit-options' + (q.key === 'usual' ? ' aw-fit-options--sizes' : q.key === 'body' ? ' aw-fit-options--body' : '');
      q.answers.forEach(([value, label]) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'aw-fit-choice';
        b.dataset.fitAnswer = value;
        if (q.key === 'body') {
          const art = document.createElement('span');
          art.className = 'aw-fit-figure';
          art.innerHTML = this.bodyIllustration(value);
          const text = document.createElement('span');
          text.className = 'aw-fit-body-label';
          text.textContent = label;
          b.append(art, text);
        } else b.textContent = label;
        b.setAttribute('aria-pressed', String(this.answers[q.key] === value));
        options.append(b);
      });
      this.el('back').hidden = this.step === 0;
      this.el('restart').hidden = true;
      this.el('unlisted').hidden = this.step !== 0;
      if (this.dialog.open) this.el('title').focus();
    }
    showResult(result) {
      clearTimeout(this.selectionTimer);
      const c = this.copy;
      this.el('options').hidden = true;
      this.el('help').hidden = true;
      this.el('progress').hidden = true;
      this.el('meter').hidden = true;
      this.el('result').hidden = false;
      this.el('back').hidden = true;
      this.el('unlisted').hidden = true;
      this.el('restart').hidden = false;
      this.el('size').textContent = result.size || '';
      this.el('status').textContent = '';
      this.el('title').textContent = result.status === 'recommended' ? c.suggested : c.range_title;
      if (result.status !== 'recommended') {
        this.el('reason').textContent = c[result.status] || c.unsupported;
      } else {
        this.el('reason').textContent = result.capped ? c.largest_reason : result.adjustment ? c.up_reason + ' ' + result.reasons.map(r => c[r + '_reason']).join(' ') : c.same_reason;
        this.selectSize(result.size);
      }
      this.el('title').focus();
    }
    selectSize(size) {
      const info = this.closest('product-info');
      const picker = info?.querySelector('variant-selects');
      const selected = picker ? [...picker.querySelectorAll('select, fieldset')].map(group => group.tagName === 'SELECT' ? group.value : group.querySelector('input:checked')?.value) : [];
      const variant = AWFitLogic.matchingVariant(this.data.variants, this.data.sizeIndex, selected, size);
      if (!picker || selected.length !== this.data.variants[0]?.options.length) { this.el('status').textContent = this.copy.manual; return; }
      if (!variant || !variant.available) { this.el('status').textContent = this.copy.unavailable; return; }
      const group = [...picker.querySelectorAll('select, fieldset')][this.data.sizeIndex];
      const form = document.getElementById(this.dataset.formId);
      const input = form?.querySelector('[name="id"]');
      if (String(input?.value) === String(variant.id)) { this.el('status').textContent = this.copy.selected; return; }
      if (group?.tagName === 'SELECT') {
        group.value = size;
        group.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        const radio = [...(group?.querySelectorAll('input[type="radio"]') || [])].find(r => r.value === size);
        if (!radio || radio.disabled) { this.el('status').textContent = this.copy.manual; return; }
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.el('status').textContent = this.copy.checking;
      const start = Date.now();
      const check = () => {
        if (!this.isConnected) return;
        const current = document.getElementById(this.dataset.formId)?.querySelector('[name="id"]');
        if (String(current?.value) === String(variant.id)) { this.el('status').textContent = this.copy.selected; return; }
        if (Date.now() - start > 8000) { this.el('status').textContent = this.copy.manual; return; }
        this.selectionTimer = setTimeout(check, 100);
      };
      check();
    }
  }
  customElements.define('aw-fit-finder', AWFitFinder);
})();
